import { Maybe, TimeZoneId, ZonedDateTime, ZonedDateTimeDisplay } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { getTimeZoneId } from '../util/DateUtil';
import { ZonedDateTimeFormatter } from '../util/ZonedDateTimeFormatter';
import { ZonedDateTimeDisplayMapperOptions } from '../model/ZonedDateTimeDisplayMapperOptions';

const toZonedDateTimeDisplay = (
  zonedTimestamp: Maybe<ZonedDateTime>,
  {
    isRequired,
    hideMidnight,
    placeholder,
    displayZone,
  }: ZonedDateTimeDisplayMapperOptions & {
    displayZone: Maybe<TimeZoneId>;
  } = {
    isRequired: false,
    hideMidnight: false,
    placeholder: undefined,
    displayZone: undefined,
  },
): ZonedDateTimeDisplay => {
  const isMissing = !!isRequired && !zonedTimestamp;
  const isSet = !!zonedTimestamp;
  const dateStr = zonedTimestamp ? ZonedDateTimeFormatter.toIsoDateString(zonedTimestamp, displayZone) : undefined;
  const date = dateStr ?? placeholder ?? '-';
  const timeStr = zonedTimestamp ? ZonedDateTimeFormatter.toTimeString(zonedTimestamp, false, displayZone) : undefined;
  const time = !!hideMidnight && timeStr === '00:00' ? undefined : timeStr;
  const dateTime = [date, time].filter(isDefined).join(' ');
  const dateTimeOrDefaultPlaceholder = dateStr ? dateTime : '-';
  const dateOrDefaultPlaceholder = dateStr ?? '-';
  const offset =
    zonedTimestamp && displayZone
      ? ZonedDateTimeFormatter.formatUtcOffsetString({
          ...zonedTimestamp,
          zone: displayZone,
        })
      : undefined;

  return {
    dateTime,
    dateTimeOrDefaultPlaceholder,
    date,
    dateOrDefaultPlaceholder,
    time,
    isRequired: !!isRequired,
    isMissing,
    isSet,
    zone: displayZone,
    offset,
  };
};

const toDisplayInTimestampTimezone = (
  zonedTimestamp: Maybe<ZonedDateTime>,
  options?: ZonedDateTimeDisplayMapperOptions,
): ZonedDateTimeDisplay =>
  toZonedDateTimeDisplay(zonedTimestamp, {
    ...options,
    displayZone: zonedTimestamp?.zone,
  });

const toDisplayInLocalTimezone = (
  zonedTimestamp: Maybe<ZonedDateTime>,
  options?: ZonedDateTimeDisplayMapperOptions,
): ZonedDateTimeDisplay =>
  toZonedDateTimeDisplay(zonedTimestamp, {
    ...options,
    displayZone: getTimeZoneId(),
  });

export const ZonedDateTimeDisplayMapper = {
  toDisplayInTimestampTimezone,
  toDisplayInLocalTimezone,
};
